import { configs } from "./configs";

const PEXELS_IMAGES_API_BASE_URL = "https://api.pexels.com/v1";
const PEXELS_VIDEOS_API_BASE_URL = "https://api.pexels.com/videos";

// Pexels API Types
export interface PexelsPhoto {
	id: number;
	width: number;
	height: number;
	url: string;
	photographer: string;
	photographer_url: string;
	photographer_id: number;
	avg_color: string;
	src: {
		original: string;
		large2x: string;
		large: string;
		medium: string;
		small: string;
		portrait: string;
		landscape: string;
		tiny: string;
	};
	liked: boolean;
	alt: string;
}

export interface PexelsVideo {
	id: number;
	width: number;
	height: number;
	url: string;
	image: string;
	duration: number;
	user: {
		id: number;
		name: string;
		url: string;
	};
	video_files: Array<{
		id: number;
		quality: string;
		file_type: string;
		width: number;
		height: number;
		fps: number;
		link: string;
	}>;
	video_pictures: Array<{
		id: number;
		picture: string;
		nr: number;
	}>;
}

export interface PexelsImageSearchResponse {
	total_results: number;
	page: number;
	per_page: number;
	photos: PexelsPhoto[];
	next_page?: string;
	prev_page?: string;
}

export interface PexelsVideoSearchResponse {
	total_results: number;
	page: number;
	per_page: number;
	videos: PexelsVideo[];
	next_page?: string;
	prev_page?: string;
}

export interface PexelsAsset {
	id: string;
	fileUrl: string;
	fileType: "video" | "image";
	pexelsId: number;
	width: number;
	height: number;
	duration?: number; // Only for videos
	metadata?: {
		photographer?: string;
		photographer_url?: string;
		alt?: string;
		avg_color?: string;
		user?: {
			id: number;
			name: string;
			url: string;
		};
		video_files?: Array<{
			id: number;
			quality: string;
			file_type: string;
			width: number;
			height: number;
			fps: number;
			link: string;
		}>;
		video_pictures?: Array<{
			id: number;
			picture: string;
			nr: number;
		}>;
	};
}

export interface SearchPexelsOptions {
	perPage?: number;
	page?: number;
}

export interface BestMatchCriteria {
	duration?: number; // Minimum duration for videos (in seconds)
	aspectRatio?: "vertical" | "horizontal" | "square"; // Preferred aspect ratio
	minWidth?: number;
	minHeight?: number;
}

/**
 * Search Pexels for images
 */
export async function searchPexelsImages(
	query: string,
	options?: SearchPexelsOptions,
): Promise<PexelsImageSearchResponse> {
	const apiKey = configs.pexels.apiKey;

	if (!apiKey) {
		throw new Error("Pexels API key not configured");
	}

	const page = options?.page || 1;
	const perPage = options?.perPage || 20;

	const url = `${PEXELS_IMAGES_API_BASE_URL}/search?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}`;

	const response = await fetch(url, {
		headers: {
			Authorization: apiKey,
		},
	});

	if (!response.ok) {
		throw new Error(`Pexels API error: ${response.status}`);
	}

	return await response.json();
}

/**
 * Search Pexels for videos
 */
export async function searchPexelsVideos(
	query: string,
	options?: SearchPexelsOptions,
): Promise<PexelsVideoSearchResponse> {
	const apiKey = configs.pexels.apiKey;

	if (!apiKey) {
		throw new Error("Pexels API key not configured");
	}

	const page = options?.page || 1;
	const perPage = options?.perPage || 15;

	const url = `${PEXELS_VIDEOS_API_BASE_URL}/search?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}`;

	const response = await fetch(url, {
		headers: {
			Authorization: apiKey,
		},
	});

	if (!response.ok) {
		throw new Error(`Pexels API error: ${response.status}`);
	}

	return await response.json();
}

/**
 * Convert Pexels photo to standardized asset format
 */
function photoToAsset(photo: PexelsPhoto): PexelsAsset {
	// Use large2x for better quality, or original as fallback
	const fileUrl = photo.src.large2x || photo.src.original;

	return {
		id: `pexels_${photo.id}`,
		fileUrl,
		fileType: "image",
		pexelsId: photo.id,
		width: photo.width,
		height: photo.height,
		metadata: {
			photographer: photo.photographer,
			photographer_url: photo.photographer_url,
			alt: photo.alt,
			avg_color: photo.avg_color,
		},
	};
}

/**
 * Convert Pexels video to standardized asset format
 */
function videoToAsset(video: PexelsVideo): PexelsAsset {
	// Find the best quality video file (prefer HD or SD)
	const videoFile =
		video.video_files.find(
			(file) => file.quality === "hd" || file.quality === "sd",
		) || video.video_files[0];

	if (!videoFile) {
		throw new Error(`Video ${video.id} has no video files`);
	}

	return {
		id: `pexels_video_${video.id}`,
		fileUrl: videoFile.link,
		fileType: "video",
		pexelsId: video.id,
		width: video.width,
		height: video.height,
		duration: video.duration,
		metadata: {
			user: video.user,
			video_files: video.video_files,
			video_pictures: video.video_pictures,
		},
	};
}

/**
 * Map pixel dimensions to Pexels aspect preference.
 * Thresholds match {@link calculateAspectRatioScore} (width/height of the frame).
 */
export function inferPexelsAspectRatioPreference(
	width: number,
	height: number,
): "vertical" | "horizontal" | "square" {
	if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
		return "vertical";

	const aspectRatio = width / height;
	if (aspectRatio < 0.8) return "vertical";
	if (aspectRatio > 1.2) return "horizontal";
	return "square";
}

/**
 * Calculate aspect ratio score (0-1, higher is better match)
 */
function calculateAspectRatioScore(
	width: number,
	height: number,
	preferred?: "vertical" | "horizontal" | "square",
): number {
	if (!preferred) return 1; // No preference, all are equally good

	const aspectRatio = width / height;
	const isVertical = aspectRatio < 0.8;
	const isHorizontal = aspectRatio > 1.2;
	const isSquare = aspectRatio >= 0.8 && aspectRatio <= 1.2;

	if (preferred === "vertical" && isVertical) return 1;
	if (preferred === "horizontal" && isHorizontal) return 1;
	if (preferred === "square" && isSquare) return 1;

	// Partial score for close matches
	if (preferred === "vertical" && aspectRatio < 1.0) return 0.7;
	if (preferred === "horizontal" && aspectRatio > 1.0) return 0.7;

	return 0.3; // Poor match
}

/**
 * Find the best matching asset from Pexels search results
 */
export function getBestMatchingAsset(
	assets: PexelsAsset[],
	criteria?: BestMatchCriteria,
): PexelsAsset | null {
	if (assets.length === 0) {
		return null;
	}

	if (!criteria) {
		// No criteria, return first asset
		return assets[0];
	}

	// Score each asset
	const scoredAssets = assets.map((asset) => {
		let score = 0;

		// Duration matching (for videos)
		if (criteria.duration && asset.duration) {
			if (asset.duration >= criteria.duration) {
				score += 1.0; // Perfect match
			} else if (asset.duration >= criteria.duration * 0.8) {
				score += 0.7; // Close match
			} else {
				score += 0.3; // Too short
			}
		} else if (!criteria.duration || asset.fileType === "image") {
			score += 0.5; // Neutral score when duration doesn't apply
		}

		// Aspect ratio matching
		const aspectRatioScore = calculateAspectRatioScore(
			asset.width,
			asset.height,
			criteria.aspectRatio,
		);
		score += aspectRatioScore * 0.5;

		// Minimum dimensions
		if (criteria.minWidth && asset.width >= criteria.minWidth) {
			score += 0.2;
		}
		if (criteria.minHeight && asset.height >= criteria.minHeight) {
			score += 0.2;
		}

		return { asset, score };
	});

	// Sort by score (highest first)
	scoredAssets.sort((a, b) => b.score - a.score);

	// Return the best match (or null if all scores are too low)
	const bestMatch = scoredAssets[0];
	if (bestMatch.score < 0.3) {
		return null; // All matches are poor
	}

	return bestMatch.asset;
}

/**
 * Search and get the best matching Pexels asset (image or video)
 */
export async function searchAndGetBestPexelsAsset(
	query: string,
	preferredType: "video" | "image" | "both",
	criteria?: BestMatchCriteria,
	options?: SearchPexelsOptions,
): Promise<PexelsAsset | null> {
	try {
		const allAssetsPromises: Promise<PexelsAsset[]>[] = [];

		// Search for videos if needed
		if (preferredType === "video" || preferredType === "both") {
			allAssetsPromises.push(
				searchPexelsVideos(query, options).then((videoResponse) =>
					videoResponse.videos
						.map((video) => {
							try {
								return videoToAsset(video);
							} catch (error) {
								console.warn(`Failed to convert video ${video.id}:`, error);
								return null;
							}
						})
						.filter((asset): asset is PexelsAsset => asset !== null),
				),
			);
		}

		// Search for images if needed
		if (preferredType === "image" || preferredType === "both") {
			allAssetsPromises.push(
				searchPexelsImages(query, options).then((imageResponse) =>
					imageResponse.photos.map((photo) => photoToAsset(photo)),
				),
			);
		}

		const allAssetsArrays = await Promise.all(allAssetsPromises);
		const assets = allAssetsArrays.flat();

		if (assets.length === 0) {
			return null;
		}

		// Get the best matching asset
		return getBestMatchingAsset(assets, criteria);
	} catch (error) {
		console.error("Error searching Pexels:", error);
		throw error;
	}
}

